import { useState, useEffect, useCallback, useRef } from 'react';
import Papa from 'papaparse';
import { useSnackbar, closeSnackbar } from 'notistack';
import ReactMarkdown from 'react-markdown';
// @mui
import Card from '@mui/material/Card';
import Container from '@mui/material/Container';
import CardHeader from '@mui/material/CardHeader';
import CardContent from '@mui/material/CardContent';
import Stack from "@mui/material/Stack";
import {alpha, useTheme} from '@mui/material/styles';
import { Grid, Skeleton, Typography } from '@mui/material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Iconify from '../../components/iconify';
import { getFinancialData, getTransactionData, Upload, createFinancialFile } from '../../components/upload';
import { useSettingsContext } from '../../components/settings';
import { fEuroNumber } from '../../utils/format-number';
import AnalyticsWidgetSummary from './analytics-widget-summary';
import AnalysisService from '../../services/analysis';
import FinancialService from '../../services/financial';

// ----------------------------------------------------------------------

const markdownStyles = {
  textAlign: 'justify',
  lineHeight: 1.8,
  '& strong': { 
    color: 'primary.main', 
    fontWeight: 700 
  },
  '& ul': { 
    listStyle: 'none', 
    p: 0, 
    m: 0 
  },
  '& li': { 
    mb: 2, 
    p: 2, 
    borderRadius: 1.5, 
    border: (theme) => `1px solid ${theme.palette.divider}`,
    borderLeft: (theme) => `4px solid ${theme.palette.primary.main}`,
    boxShadow: (theme) => theme.customShadows.z1,
  },
  '& p': { mb: 1 }
};

export default function FinancialDragNDrop() {
    const theme = useTheme();
    const settings = useSettingsContext();
    const { enqueueSnackbar } = useSnackbar();
    const [file, setFile] = useState(null);
    const [data, setData] = useState(null);
    const [generalResume, setGeneralResume] = useState(localStorage.getItem("ai_resume") || "");
    const [isGenerating, setIsGenerating] = useState(false);
    const [isUploading, setIsUploading] = useState(false);

    // Usar useRef para controlar si ya se hizo la llamada
    const hasFetchedRef = useRef(false);
    const isFetchingRef = useRef(false);

     useEffect(() => {
        const savedResume = localStorage.getItem("ai_resume");
        const savedScore = localStorage.getItem("ai_score_valid");
        
        if (savedResume) {
            setGeneralResume(savedResume);
        }
    }, []);

    const aiValid = localStorage.getItem("ai_score_valid") 
        ? JSON.parse(localStorage.getItem("ai_score_valid")) 
        : null;
    
    useEffect(() => {
        try {
            const stored = getFinancialData();
            if (stored) {
                setData(stored);
            }
        } catch (error) {
            console.error("Error al obtener los datos:", error);
            settings.onFilesFailed();
        }
    }, [settings]);

    // Efecto para generar el análisis SOLO cuando haya datos nuevos
    useEffect(() => {
        const fetchResume = async () => {
            // 1. Añadimos hasFetchedRef.current a la validación inicial
            if (!data || isGenerating || isUploading || isFetchingRef.current || hasFetchedRef.current) return;
            
            // Verificar si ya tenemos el análisis para estos datos
            const savedResume = localStorage.getItem("ai_resume");
            if (savedResume) {
                setGeneralResume(savedResume);
                hasFetchedRef.current = true;
                return;
            }

            // 2. Marcamos como "leído" ANTES del try/catch para bloquear el loop en caso de error
            hasFetchedRef.current = true;
            isFetchingRef.current = true;
            setIsGenerating(true);
            
            try {
                const contextData = {
                    alertas: data.alertas,
                    abono: data.abono,
                    gasto: data.gasto,
                    transaccional_categorizado: data.categorizado.slice(0, 1000).map(t => ({
                        concepto: t.concepts,
                        categoria: t.aux_detalle_export,
                        cantidad: t.amount,
                        fecha: t.value_date,
                    })),
                    saldo_histórico: data.saldo?.saldo_diario || [],
                    n_transacciones_total: data.alertas?.n_transacciones_total || 0,
                    dias_profundidad_agregacion: data.alertas?.dias_profundidad_agregacion || 0,
                    dias_sin_transacciones: data.alertas?.dias_sin_transacciones || 0,
                };

                const result = await AnalysisService.getFinancialAnalysis(contextData);
                
                if (result && result.analysis) {
                    setGeneralResume(result.analysis);
                    localStorage.setItem("ai_resume", result.analysis);
                    
                    const isOk = result.analysis.includes("OK - Encaja con la cuenta principal");
                    const isFraud = result.analysis.includes("Revisar - Fraude potencial");
                    const isAccountValid = isOk && !isFraud;
                    
                    localStorage.setItem("ai_score_valid", JSON.stringify(isAccountValid));
                }
                
            } catch (error) {
                console.error("Error al generar resumen IA:", error);
                enqueueSnackbar("Error al generar el análisis con IA", { variant: 'error' });
                // Como hasFetchedRef.current ya es true arriba, no volverá a intentar infinitamente
            } finally {
                setIsGenerating(false);
                isFetchingRef.current = false;
            }
        };

        fetchResume();
    // 3. Reducimos las dependencias para evitar triggers innecesarios
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data]);

    const handleDropSingleFile = useCallback(
        async (acceptedFiles) => {
            const newFile = acceptedFiles[0];
            if (!newFile) return;

            setFile(newFile);
            const snackbarId = enqueueSnackbar(`Se está procesando su archivo`, { variant: 'info', persist: true });

            setIsUploading(true);
            
            // Resetear estados y localStorage
            localStorage.removeItem("ai_resume");
            localStorage.removeItem("ai_score_valid");
            setGeneralResume("");
            hasFetchedRef.current = false;  // Resetear el ref para permitir nueva llamada
            isFetchingRef.current = false;
            settings.setDetailsCache(null);
            settings.setSaldoCache(null);

            try {
                const formData = new FormData();
                formData.append("file", newFile);

                const response = await FinancialService.getData(formData);
                
                console.log("Respuesta del backend:", response);

                if (response.status === "failed_input_check") {
                    enqueueSnackbar(`Archivo no válido: ${response.json.input.mensaje}`, { variant: 'error' });
                    closeSnackbar(snackbarId);
                    settings.onFilesFailed();
                    setIsUploading(false);
                    return;
                }

                // Guardar datos procesados
                localStorage.setItem("fileName", newFile.name);
                localStorage.setItem("financeData", JSON.stringify(response));
                
                const resumen = getFinancialData();
                setData(resumen);

                if (resumen && resumen.categorizado) {
                    localStorage.setItem("transactionData", JSON.stringify(resumen.categorizado));
                }
                
                closeSnackbar(snackbarId);
                
                if (response.json?.agb?.valido === true) {
                    enqueueSnackbar("Archivo procesado correctamente", { variant: 'success' });
                    settings.onValid();
                } else {
                    enqueueSnackbar("Archivo no válido", { variant: 'warning' });
                    settings.onNonValid();
                }
                settings.onFiles();
                setIsUploading(false);
                
            } catch (error) {
                closeSnackbar(snackbarId);
                setIsUploading(false);
                
                if (error.response?.status === 403) {
                    enqueueSnackbar(`Número de usos agotado`, { variant: 'warning' });
                    return;
                }

                if (error.response?.status === 400) {
                    enqueueSnackbar(`Archivo no válido: ${error.response.data.error}`, { variant: 'error' });
                    return;
                }
                enqueueSnackbar("Error procesando el archivo", { variant: 'error' });
                settings.onFilesFailed();
                console.error("Error procesando archivo:", error);
            }
        },
        [settings, enqueueSnackbar]
    );

    const fileName = localStorage.getItem('fileName')
    
    const handleDownloadJSON = () => {
        const blob = createFinancialFile();
        if (!blob) {
          console.error("No se pudo generar el archivo.");
          return;
        }

        const fileNameWithoutExtension = fileName ? fileName.replace(/\.[^/.]+$/, "") : "";

        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `resultado_agb_${fileNameWithoutExtension}.xlsx`;
        link.click();
        URL.revokeObjectURL(url);
    };

    return (
      <Container sx={{ my: 10 }}>
        <Stack spacing={2}>
          {(settings.files && data) &&
            <Stack spacing={2}>
              <Card spacing={2}>
                <CardHeader
                  title={
                    <Typography variant="h5">Características de la agregación</Typography>
                  }
                />
                  <CardContent>
                    <Grid container spacing={2}>
                      <Grid item xs={6} md={3}>
                      <AnalyticsWidgetSummary
                        title="Días de profundidad"
                        total={data.alertas.dias_profundidad_agregacion || '0'}
                        icon={<Iconify icon="solar:calendar-bold-duotone" width={64} />}
                      />
                      </Grid>

                      <Grid item xs={6} md={3}>
                      <AnalyticsWidgetSummary
                        title="Número de movimientos"
                        total={data.alertas.n_transacciones_total || '0'}
                        funct={fEuroNumber}
                        icon={<Iconify icon="fluent:number-symbol-square-24-filled" width={64} />}
                      />
                      </Grid>

                      <Grid item xs={6} md={3}>
                      <AnalyticsWidgetSummary
                        title="Días sin movimientos"
                        total={data.alertas.dias_sin_transacciones || '0'}
                        icon={<Iconify icon="oui:cross-in-circle-filled" width={58} />}
                      />
                      </Grid>

                      <Grid item xs={6} md={3}>
                      <AnalyticsWidgetSummary
                        title="Número de cuentas"
                        total={data.alertas.num_cuentas || '0'}
                        icon={<Iconify icon="mdi:file-account-outline" width={58} />}
                      />
                      </Grid>
                    </Grid>
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} marginTop={2}>
                      <Card sx={{ flex: 1 }}>
                        <CardHeader title="Agregación analizada" />
                        <CardContent>
                        <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" height="100%">
                          <Typography variant="h6" sx={{ mb: 2 }}>
                          {fileName}
                          </Typography>
                          <Box
                          title={
                            data.esValido !== true
                            ? 'No puedes descargar el resultado porque la agregación no es válida'
                            : undefined
                          }
                          >
                          <Button
                            variant="contained"
                            startIcon={<Iconify icon="material-symbols:download" width={30} />}
                            onClick={handleDownloadJSON}
                            disabled={data.esValido !== true}
                          >
                            Descargar resultado
                          </Button>
                          </Box>
                        </Box>
                        </CardContent>
                      </Card>
                    <Box sx={{ flex: 1, display: 'flex' }}>
                      {
                        data.esValido ?
                          <AnalyticsWidgetSummary
                            sx={{ flex: 1 }}
                            title=""
                            total='Agregación válida'
                            icon={<Iconify icon="mdi:check-bold" width={58} color={theme.palette.tertiary.main} />}
                          />
                          :
                          <AnalyticsWidgetSummary
                            sx={{ flex: 1 }}
                            title={
                              <Typography variant="subtitle2" sx={{ whiteSpace: 'pre-line' }}>{data.validoMessage || "Agregación no válida"}</Typography>
                            }
                            total=''
                            icon={<Iconify icon="material-symbols:warning-outline" width={58} color={theme.palette.warning.main} />}
                          />
                      }

                    </Box>
                  </Stack>
                  <Stack direction="row" spacing={2} marginTop={2}>
                    <Card sx={{ flex: 1, borderRadius: 2, border: (t) => `1px solid ${t.palette.divider}` }}>
                      <CardHeader
                        avatar={
                          <Box
                            sx={{
                              width: 40,
                              height: 40,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              borderRadius: '10px',
                            }}
                          >
                            <Iconify icon="material-icon-theme:gemini-ai" width={35} />
                          </Box>
                        }
                        title={<Typography variant="h6">Análisis del agente</Typography>}
                        subheader="Perspectiva de la completitud de la situación financiera del cliente"
                        sx={{ borderBottom: (t) => `1px dashed ${t.palette.divider}`, py: 2 }}
                      />

                      <CardContent sx={{ p: 3 }}>
                        <Stack spacing={3}> 
                          <Card sx={{ 
                              p: 2, 
                              display: 'flex', 
                              alignItems: 'center', 
                              justifyContent: 'space-between',
                              transition: 'all 0.3s ease-in-out',
                              border: (t) => {
                                  if (isGenerating || isUploading) return `1px dashed ${t.palette.divider}`;
                                  return `1px solid ${aiValid === false ? t.palette.red.light : t.palette.divider}`;
                              },
                              bgcolor: (t) => {
                                  if (isGenerating || isUploading) return alpha(t.palette.background.neutral, 0.5);
                                  return aiValid === false ? alpha(t.palette.red.main, 0.04) : alpha(t.palette.tertiary.main, 0.04);
                              },
                          }}>
                              <Stack direction="row" alignItems="center" spacing={2} sx={{ width: '100%' }}>
                                  <Box sx={{ 
                                      p: 1.5, 
                                      borderRadius: '12px', 
                                      bgcolor: (t) => isGenerating || isUploading ? alpha(t.palette.grey[500], 0.1) : alpha(aiValid === false ? t.palette.red.main : t.palette.tertiary.main, 0.1) 
                                  }}>
                                      {isGenerating || isUploading ? (
                                          <Iconify icon="eos-icons:loading" width={32} color="text.disabled" />
                                      ) : (
                                          <Iconify 
                                              icon={aiValid === false ? "solar:shield-warning-bold-duotone" : "solar:shield-check-bold-duotone"} 
                                              width={32} 
                                              color={aiValid === false ? theme.palette.red.main : theme.palette.tertiary.main} 
                                          />
                                      )}
                                  </Box>
                                  <Box sx={{ flexGrow: 1 }}>
                                      <Typography variant="h6">Dictamen de integridad</Typography>
                                      {isGenerating || isUploading ? (
                                          <Skeleton variant="text" width="60%" height={20} />
                                      ) : (
                                          <Typography variant="body2" sx={{ color: aiValid === false ? 'red.main' : 'text.secondary' }}>
                                              {aiValid === false 
                                                  ? "Se han detectado patrones inconsistentes que sugieren el ocultamiento de información financiera." 
                                                  : "La cuenta analizada presenta un comportamiento coherente con una cuenta corriente principal."}
                                          </Typography>
                                      )}
                                  </Box>
                                  {!isGenerating && !isUploading && (
                                      <Typography variant="h5" sx={{ 
                                          color: aiValid === false ? 'red.main' : 'tertiary.main',
                                          fontWeight: 'bold',
                                          textTransform: 'uppercase',
                                          ml: 2
                                      }}>
                                          {aiValid === false ? 'Fraude Potencial' : 'Verificado'}
                                      </Typography>
                                  )}
                              </Stack>
                          </Card>
                          <Box>
                              {isGenerating || !generalResume ? (
                                <Stack spacing={2}>
                                  <Skeleton variant="text" width="40%" height={25} />
                                  <Skeleton variant="rounded" height={60} />
                                  <Skeleton variant="text" width="30%" height={25} />
                                  <Skeleton variant="rounded" height={60} />
                                </Stack>
                              ) : (
                                <Box sx={markdownStyles}>
                                  <ReactMarkdown>
                                    {generalResume}
                                  </ReactMarkdown>
                                </Box>
                              )}
                          </Box>
                        </Stack>
                      </CardContent>
                    </Card>
                  </Stack>
                </CardContent>
              </Card>
            </Stack>
          }
          <Card>
            <CardHeader title="Subir archivo" />
            <CardContent>
              <Upload
                accept={{
                  '*/*': ['.*'],
                }}
                file={file}
                onDrop={handleDropSingleFile}
                onDelete={() => setFile(null)}
              />
            </CardContent>
          </Card>
        </Stack>
      </Container>
    );
}

function parseEuropeanDate(dateStr) {
  const [day, month, year] = dateStr.split('/');
  return new Date(`${year}-${month}-${day}`);
}

function sortRowsByDateDesc(rows) {
  return rows
    .filter((row) => row.value_date)
    .sort((a, b) => parseEuropeanDate(b.value_date) - parseEuropeanDate(a.value_date));
}